import './signUpReturningUserPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { Input } from '../../components/Input/Input';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UserExistsExceptionCode } from '../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { userExistsException } = useUserStore();

  const { primaryPhoneNumber, primaryEmailAddress } = useSignUpStore();

  const existingPhoneNumber = useMemo((): boolean => {
    return (
      userExistsException === UserExistsExceptionCode.PHONE_NUMBER_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  const existingEmailAddress = useMemo((): boolean => {
    return (
      userExistsException ===
        UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  // CP-6949: only the email matched, so the phone entered is not on the
  // account and cannot be added to it from here
  const isEmailOnlyMatch =
    userExistsException === UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED;

  // CP-6949: the form that sent the user here left its refused delivery in
  // pinStore. This page explains it instead, so clear it, or the sign-in and
  // sign-up pages show it again as soon as the user leaves
  useEffect((): void => {
    usePinStore.setState({
      deliverPinErrorCode: null,
      deliverPinFailedMessage: null,
    });
  }, []);

  const back = (): void => {
    navigate('/sign-up');
  };

  const signIn = (): void => {
    usePinStore.setState({
      primaryPhoneNumber: existingPhoneNumber ? primaryPhoneNumber : null,
    });

    navigate('/sign-in');
  };

  return (
    <div className="sign-up-returning-user-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            trailing={
              <Link
                data-testid={
                  locator.pages.signUpReturningUser.header.signInLink
                }
                to="/sign-in"
              >
                {t('SignUpReturningUser.signin')}
              </Link>
            }
            showLogo
            splitLayout
          />

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signUpReturningUser.form.root}
              noValidate
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUpReturningUser.form.h1}>
                  {isEmailOnlyMatch
                    ? t('SignUpReturningUser.ownershipUnverifiedTitle')
                    : t('SignUpReturningUser.title')}
                </h1>
                <h2 data-testid={locator.pages.signUpReturningUser.form.h2}>
                  {isEmailOnlyMatch
                    ? t('SignUpReturningUser.ownershipUnverifiedSubtitle')
                    : t('SignUpReturningUser.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  {existingPhoneNumber && (
                    <div style={{ marginBottom: '2rem' }}>
                      <NumberInput
                        data-testid={
                          locator.pages.signUpReturningUser.form.phoneInput
                        }
                        label={t('SignUpReturningUser.phone')}
                        value={primaryPhoneNumber}
                        disabled
                        phone
                      />
                    </div>
                  )}

                  {existingEmailAddress && (
                    <Input
                      data-testid={
                        locator.pages.signUpReturningUser.form.emailInput
                      }
                      label={t('SignUpReturningUser.email')}
                      style={{ pointerEvents: 'none' }}
                      value={primaryEmailAddress}
                      disabled
                    />
                  )}
                </div>

                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.signUpReturningUser.form.backButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={back}
                  >
                    {t('SignUpReturningUser.back')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.signUpReturningUser.form.signInButton
                    }
                    fullWidth
                    onClick={signIn}
                  >
                    {t('SignUpReturningUser.signin')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.signUpReturningUser.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpReturningUserPage';
